import React, { useState, useMemo } from 'react';
import { ChevronLeft, ChevronRight, Clock, User, CalendarDays } from 'lucide-react';

const MONTHS = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
const WEEKDAYS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

const toKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const hhmm = (t) => (t ? String(t).slice(0, 5) : '');
const firstName = (name) => (name ? String(name).trim().split(' ')[0] : 'Client');
// Interventions réellement réservées (on ignore les états annulés / expirés / abandonnés)
const BOOKED_STATUSES = ['confirmee', 'en_cours', 'terminee', 'en_attente'];
const STRONG_STATUSES = ['confirmee', 'en_cours'];

export const EmployeePlanningCalendar = ({ interventions = [], planning = [], onSelectIntervention }) => {
  const [cursor, setCursor] = useState(() => {
    const n = new Date();
    return new Date(n.getFullYear(), n.getMonth(), 1);
  });

  const todayKey = toKey(new Date());
  const year = cursor.getFullYear();
  const month = cursor.getMonth();

  const byDate = useMemo(() => {
    const m = {};
    (interventions || []).forEach((iv) => {
      if (!iv.date || !BOOKED_STATUSES.includes(iv.status)) return;
      const k = String(iv.date).slice(0, 10);
      (m[k] = m[k] || []).push(iv);
    });
    Object.values(m).forEach((list) => list.sort((a, b) => hhmm(a.start_time).localeCompare(hhmm(b.start_time))));
    return m;
  }, [interventions]);

  const availByDate = useMemo(() => {
    const m = {};
    (planning || []).forEach((s) => {
      if (s.is_available && s.date) {
        const k = String(s.date).slice(0, 10);
        m[k] = (m[k] || 0) + 1;
      }
    });
    return m;
  }, [planning]);

  const weeks = useMemo(() => {
    const first = new Date(year, month, 1);
    const startDow = (first.getDay() + 6) % 7; // lundi = 0
    const start = new Date(year, month, 1 - startDow);
    const d = new Date(start);
    const out = [];
    for (let wi = 0; wi < 6; wi++) {
      const row = [];
      for (let di = 0; di < 7; di++) {
        row.push(new Date(d));
        d.setDate(d.getDate() + 1);
      }
      out.push(row);
    }
    return out.filter((row) => row.some((dd) => dd.getMonth() === month));
  }, [year, month]);

  // Agenda chronologique du mois courant (pour mobile)
  const monthAgenda = useMemo(() => {
    const items = [];
    Object.keys(byDate).forEach((k) => {
      const [y, mo] = k.split('-').map(Number);
      if (y === year && mo === month + 1) byDate[k].forEach((iv) => items.push(iv));
    });
    items.sort((a, b) => (a.date + hhmm(a.start_time)).localeCompare(b.date + hhmm(b.start_time)));
    return items;
  }, [byDate, year, month]);

  const goPrev = () => setCursor(new Date(year, month - 1, 1));
  const goNext = () => setCursor(new Date(year, month + 1, 1));
  const goToday = () => {
    const n = new Date();
    setCursor(new Date(n.getFullYear(), n.getMonth(), 1));
  };

  const chipClass = (iv) =>
    STRONG_STATUSES.includes(iv.status)
      ? 'bg-[#A5BBA8] text-white'
      : 'bg-[#A5BBA8]/25 text-[#2A312C]';

  return (
    <div data-testid="employee-planning-calendar">
      {/* Barre de navigation mensuelle */}
      <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <button
            onClick={goPrev}
            data-testid="planning-prev-month-btn"
            className="w-9 h-9 rounded-xl border border-[#E3DED5] flex items-center justify-center text-[#2A312C] hover:bg-[#F9F6F0] transition-colors"
            aria-label="Mois précédent"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            onClick={goNext}
            data-testid="planning-next-month-btn"
            className="w-9 h-9 rounded-xl border border-[#E3DED5] flex items-center justify-center text-[#2A312C] hover:bg-[#F9F6F0] transition-colors"
            aria-label="Mois suivant"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
          <h3 className="font-['Cormorant_Garamond'] text-2xl font-medium text-[#2A312C] ml-1" data-testid="planning-month-label">
            {MONTHS[month]} {year}
          </h3>
        </div>
        <button
          onClick={goToday}
          data-testid="planning-today-btn"
          className="text-sm px-4 py-2 rounded-xl border border-[#A5BBA8] text-[#5C6A61] hover:bg-[#A5BBA8]/10 transition-colors inline-flex items-center gap-2"
        >
          <CalendarDays className="w-4 h-4" />
          Aujourd'hui
        </button>
      </div>

      {/* Légende */}
      <div className="flex items-center gap-4 mb-3 text-xs text-[#5C6A61]">
        <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-[#A5BBA8]" /> Intervention</span>
        <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-full border border-[#A5BBA8]" /> Disponibilité</span>
      </div>

      {/* Grille calendaire (masquée sur très petit écran au profit de l'agenda) */}
      <div className="hidden sm:block border border-[#E3DED5] rounded-2xl overflow-hidden bg-white">
        <div className="grid grid-cols-7 bg-[#F9F6F0] border-b border-[#E3DED5]">
          {WEEKDAYS.map((w) => (
            <div key={w} className="py-2 text-center text-xs font-semibold text-[#5C6A61]">{w}</div>
          ))}
        </div>
        {weeks.map((row, wi) => (
          <div key={wi} className="grid grid-cols-7">
            {row.map((dd) => {
              const k = toKey(dd);
              const inMonth = dd.getMonth() === month;
              const isToday = k === todayKey;
              const dayItems = byDate[k] || [];
              const avail = availByDate[k] || 0;
              return (
                <div
                  key={k}
                  data-testid={`planning-day-${k}`}
                  className={`min-h-[92px] border-b border-r border-[#E3DED5] p-1.5 align-top ${
                    inMonth ? 'bg-white' : 'bg-[#FBFAF7] text-[#C9C3B8]'
                  }`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span
                      className={`text-xs font-semibold w-6 h-6 flex items-center justify-center rounded-full ${
                        isToday ? 'bg-[#C86B51] text-white' : inMonth ? 'text-[#2A312C]' : 'text-[#C9C3B8]'
                      }`}
                    >
                      {dd.getDate()}
                    </span>
                    {inMonth && avail > 0 && dayItems.length === 0 && (
                      <span className="text-[10px] text-[#A5BBA8]" title={`${avail} créneau(x) disponible(s)`}>· dispo</span>
                    )}
                  </div>
                  <div className="space-y-1">
                    {dayItems.slice(0, 3).map((iv) => (
                      <button
                        key={iv.reservation_id}
                        onClick={() => onSelectIntervention && onSelectIntervention(iv)}
                        data-testid={`planning-intervention-${iv.reservation_id}`}
                        className={`w-full text-left rounded-md px-1.5 py-1 text-[11px] leading-tight truncate hover:opacity-90 transition-opacity ${chipClass(iv)}`}
                        title={`${hhmm(iv.start_time)}${iv.end_time ? '–' + hhmm(iv.end_time) : ''} ${iv.client?.name || ''}`}
                      >
                        <span className="font-semibold">{hhmm(iv.start_time) || '—'}</span>
                        {' '}{firstName(iv.client?.name)}
                      </button>
                    ))}
                    {dayItems.length > 3 && (
                      <span className="block text-[10px] text-[#5C6A61] pl-1">+{dayItems.length - 3} autre(s)</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </div>

      {/* Vue agenda mobile (chronologique) */}
      <div className="sm:hidden">
        {monthAgenda.length === 0 ? (
          <div className="text-center py-10 text-[#5C6A61] text-sm">Aucune intervention ce mois-ci.</div>
        ) : (
          <div className="space-y-2">
            {monthAgenda.map((iv) => {
              const d = new Date(iv.date + 'T00:00:00');
              return (
                <button
                  key={iv.reservation_id}
                  onClick={() => onSelectIntervention && onSelectIntervention(iv)}
                  data-testid={`planning-agenda-${iv.reservation_id}`}
                  className="w-full text-left card-default flex items-center gap-3"
                >
                  <div className="w-12 text-center">
                    <p className="text-[10px] uppercase text-[#5C6A61]">{WEEKDAYS[(d.getDay() + 6) % 7]}</p>
                    <p className="text-xl font-semibold text-[#2A312C] leading-none">{d.getDate()}</p>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-[#2A312C] flex items-center gap-1.5">
                      <Clock className="w-3.5 h-3.5 text-[#A5BBA8]" />
                      {hhmm(iv.start_time) || '—'}{iv.end_time ? ` – ${hhmm(iv.end_time)}` : ''}
                    </p>
                    <p className="text-sm text-[#5C6A61] flex items-center gap-1.5 truncate">
                      <User className="w-3.5 h-3.5" />
                      {iv.client?.name || 'Client'}
                    </p>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
